/**
 * TanStack Query key factory.
 * Hierarchical key structure enables granular cache invalidation.
 *
 * Pattern:
 *   queryKeys.{domain}.all            -> invalidates all domain queries
 *   queryKeys.{domain}.list(params)   -> invalidates specific list
 *   queryKeys.{domain}.detail(id)     -> invalidates specific item
 */

export const queryKeys = {
  /** Ad Accounts */
  accounts: {
    all: () => ['accounts'] as const,
    lists: () => [...queryKeys.accounts.all(), 'list'] as const,
    list: (organizationId?: string) =>
      [...queryKeys.accounts.lists(), organizationId ?? 'none'] as const,
    details: () => [...queryKeys.accounts.all(), 'detail'] as const,
    detail: (id: string, organizationId?: string) =>
      [...queryKeys.accounts.details(), organizationId ?? 'none', id] as const,
    byPlatform: (platform: string) => [...queryKeys.accounts.all(), 'platform', platform] as const,
    metaPixels: (adAccountId: string) =>
      [...queryKeys.accounts.all(), 'meta-pixels', adAccountId] as const,
    metaPages: (adAccountId: string) =>
      [...queryKeys.accounts.all(), 'meta-pages', adAccountId] as const,
    metaPagePosts: (adAccountId: string, pageId: string) =>
      [...queryKeys.accounts.all(), 'meta-page-posts', adAccountId, pageId] as const,
  },

  /** Events */
  events: {
    all: () => ['events'] as const,
    lists: () => [...queryKeys.events.all(), 'list'] as const,
    list: (filters?: Record<string, unknown>) =>
      [...queryKeys.events.lists(), filters ?? {}] as const,
    details: () => [...queryKeys.events.all(), 'detail'] as const,
    detail: (id: string) => [...queryKeys.events.details(), id] as const,
  },

  /** Campaigns */
  campaigns: {
    all: () => ['campaigns'] as const,
    lists: () => [...queryKeys.campaigns.all(), 'list'] as const,
    list: (filters?: Record<string, unknown>) =>
      [...queryKeys.campaigns.lists(), filters ?? {}] as const,
    details: () => [...queryKeys.campaigns.all(), 'detail'] as const,
    detail: (id: string) => [...queryKeys.campaigns.details(), id] as const,
    metrics: (id: string) => [...queryKeys.campaigns.all(), 'metrics', id] as const,
    payment: (id: string) => [...queryKeys.campaigns.all(), 'payment', id] as const,
  },

  /** Targeting */
  targeting: {
    all: () => ['targeting'] as const,
    interests: (query: string) => [...queryKeys.targeting.all(), 'interests', query] as const,
    behaviors: (query: string) => [...queryKeys.targeting.all(), 'behaviors', query] as const,
    locations: (query: string) => [...queryKeys.targeting.all(), 'locations', query] as const,
  },

  /** Google Ads platform-specific search */
  adsGoogle: {
    all: () => ['ads-google'] as const,
    keywords: (adAccountId: string, query: string) =>
      [...queryKeys.adsGoogle.all(), 'keywords', adAccountId, query] as const,
    audiences: (adAccountId: string, query: string, type?: string) =>
      [...queryKeys.adsGoogle.all(), 'audiences', adAccountId, query, type ?? 'ALL'] as const,
    geoTargets: (adAccountId: string, query: string) =>
      [...queryKeys.adsGoogle.all(), 'geo-targets', adAccountId, query] as const,
  },

  /** AI Suggestions */
  ai: {
    all: () => ['ai'] as const,
    budgetSuggestion: (params: Record<string, unknown>) =>
      [...queryKeys.ai.all(), 'budget', params] as const,
    audienceSuggestion: (params: Record<string, unknown>) =>
      [...queryKeys.ai.all(), 'audience', params] as const,
    assetAccess: (key: string) => [...queryKeys.ai.all(), 'asset-access', key] as const,
  },

  /** Creatives (campaign-scoped) */
  creatives: {
    all: () => ['creatives'] as const,
    byCampaign: (campaignId: string) =>
      [...queryKeys.creatives.all(), 'campaign', campaignId] as const,
  },

  /** Creative Library (org-level, standalone) */
  creativeLibrary: {
    all: () => ['creative-library'] as const,
    lists: () => [...queryKeys.creativeLibrary.all(), 'list'] as const,
    list: (filters?: Record<string, unknown>) =>
      [...queryKeys.creativeLibrary.lists(), filters ?? {}] as const,
    details: () => [...queryKeys.creativeLibrary.all(), 'detail'] as const,
    detail: (id: string) => [...queryKeys.creativeLibrary.details(), id] as const,
  },

  /** Analytics */
  analytics: {
    all: () => ['analytics'] as const,
    dashboard: (dateRange?: Record<string, unknown>) =>
      [...queryKeys.analytics.all(), 'dashboard', dateRange ?? {}] as const,
    campaign: (id: string, dateRange?: { start: string; end: string }) =>
      [...queryKeys.analytics.all(), 'campaign', id, dateRange ?? {}] as const,
  },

  /** Notifications */
  notifications: {
    all: () => ['notifications'] as const,
    lists: () => [...queryKeys.notifications.all(), 'list'] as const,
    list: (filters?: Record<string, unknown>) =>
      [...queryKeys.notifications.lists(), filters ?? {}] as const,
    unreadCount: (organizationId?: string) =>
      [...queryKeys.notifications.all(), 'unread-count', organizationId ?? 'none'] as const,
    preferences: (organizationId?: string) =>
      [...queryKeys.notifications.all(), 'preferences', organizationId ?? 'none'] as const,
  },

  /** Admin */
  admin: {
    all: () => ['admin'] as const,
    dashboard: () => [...queryKeys.admin.all(), 'dashboard'] as const,
    campaigns: (filters?: Record<string, unknown>) =>
      [...queryKeys.admin.all(), 'campaigns', filters ?? {}] as const,
    managedAccounts: (filters?: Record<string, unknown>) =>
      [...queryKeys.admin.all(), 'managed-accounts', filters ?? {}] as const,
  },
} as const;
